import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Check, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';

interface Member {
  id: number; companyName: string; brandName?: string | null; email: string;
  status: string; walletBalance: number; isSelf: boolean;
}
interface Group { groupId: number | null; groupName: string | null; members: Member[] }

/**
 * Switch between merchant accounts owned by the same business.
 *
 * Renders NOTHING unless the account is actually linked to another, so a single-account merchant —
 * almost all of them — sees no change at all.
 *
 * After switching, the whole react-query cache is cleared before navigating home. Every cached list
 * (orders, wallet, invoices) belongs to the account you just left, and showing one company's orders
 * under another company's name for even a moment is the kind of thing that gets reported as a data
 * leak — correctly.
 */
export function AccountSwitcher() {
  const { user, switchMerchant } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const { data } = useQuery({
    queryKey: ['my-linked-accounts'],
    queryFn: async () => (await api.get<Group>('/merchants/me/linked-accounts')).data,
    staleTime: 5 * 60_000,
    // Only the main merchant login may switch; a team member stays in the account they belong to.
    enabled: user?.kind === 'merchant',
    retry: false,
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const members = data?.members ?? [];
  if (members.length < 2) return null;

  const current = members.find((m) => m.isSelf);

  const go = async (m: Member) => {
    if (m.isSelf || busyId) return;
    setBusyId(m.id);
    try {
      await switchMerchant(m.id);
      qc.clear();                       // see the note above — stale data belongs to the old account
      toast.success(`Switched to ${m.brandName || m.companyName}`);
      setOpen(false);
      window.location.href = '/dashboard';
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not switch account');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => setOpen((v) => !v)}
        title="Switch account"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 max-w-[220px]">
        <Building2 size={14} className="text-emerald-700 shrink-0" />
        <span className="text-xs font-medium truncate">{current?.brandName || current?.companyName || 'Account'}</span>
        <ChevronDown size={13} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 text-[11px] text-slate-500 border-b bg-slate-50">
            {data?.groupName ?? 'Your accounts'}
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {members.map((m) => {
              const openable = m.status === 'ACTIVE' || m.status === 'APPROVED';
              return (
                <li key={m.id}>
                  <button
                    onClick={() => go(m)}
                    disabled={m.isSelf || !openable || !!busyId}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2 ${m.isSelf ? 'bg-emerald-50/60' : 'hover:bg-slate-50'} disabled:cursor-default`}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm truncate">{m.brandName || m.companyName}</span>
                      <span className="block text-[11px] text-slate-500 truncate">
                        {openable ? `₹${Number(m.walletBalance ?? 0).toLocaleString('en-IN')}` : `${m.status} — cannot be opened`}
                      </span>
                    </span>
                    {busyId === m.id
                      ? <Loader2 size={14} className="animate-spin text-slate-400 shrink-0" />
                      : m.isSelf ? <Check size={14} className="text-emerald-600 shrink-0" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
